import React, { useEffect, useState } from 'react'
import { Params } from 'react-router-dom'
import { QueryClient } from '@tanstack/react-query'
import {
  giftDetailInfoByOrderIdQueryOption,
  giftedCouponDetailQueryOption,
  giftShareUrlInfoQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  CouponPurchasedInfo,
  GiftDetailInfoByOrderIdSpace,
  GiftedCouponDetailSpace,
  GiftShareUrlInfoSpace,
} from 'src/types/api'
import usePageNav from 'src/hooks/usePageNav.hook'
import { maskPhoneNumber } from 'src/utils'
import { Roboto_num2_b16 } from 'src/styles/fonts/RobotoFonts'
import { css, RuleSet } from 'styled-components'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import GiftPaymentPageView from 'src/pages/GiftPaymentPage/view'
import { useScript } from 'src/hooks/useScript.hook'
import { DateUtils } from 'src/utils/date'
import { useGlobalStore } from 'src/store/zustand'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { Bridge } from 'src/utils/bridge'
import { useToast } from 'src/provider/ToastContextProvider'
import { captureError, captureMessage, SENTRY_SEVERITY, toBridgeError } from 'src/utils/sentry'

export type GiftResultContentType = {
  title: string
  desc: string
  img?: { src: string; width: string }
  infoList: {
    text: string
    value: string
    topDivider?: boolean
    bottomDivider?: boolean
    valueFontStyle?: RuleSet<object>
  }[]
  notiText?: string[]
  share?: boolean
  afterPurchaseButton?: { text: string; onClickFn: () => void }
}
export interface GiftPaymentPageProps {
  resultContent: GiftResultContentType
  giftDetailData?:
    | GiftDetailInfoByOrderIdSpace.Response
    | GiftedCouponDetailSpace.Response
    | CouponPurchasedInfo.Response
  giftShareData: GiftShareUrlInfoSpace.Response
  mainPageNav: () => void
  handleShareUrl: (url: string) => void
  handleIosKakaoShare: () => void
}

const giftPaymentStatus = ['payment_success', 'payment_fail', 'payment_cancel']
const giftSendStatus = ['send_complete']

export const giftPaymentLoader =
  (queryClient: QueryClient) =>
  async ({ params: { paymentStatus, orderId } }: { params: Params<keyof MatchParams> }) => {
    if (giftPaymentStatus.includes(paymentStatus!)) {
      return Promise.allSettled([
        await queryClient.ensureQueryData(
          giftDetailInfoByOrderIdQueryOption({ OrderID: orderId! }),
        ),
      ])
    } else {
      return Promise.allSettled([
        await queryClient.ensureQueryData(
          giftedCouponDetailQueryOption({ GiftIdx: Number(orderId) }),
        ),
      ])
    }
  }

const GiftPaymentPage = () => {
  const { paymentStatus, couponNumber, couponType, exception_Idx, orderId } = useParamsType()
  const { giftPurchasePageNav, mainPageNav, giftedCouponPageNav } = usePageNav()
  const { ios, web } = useChannel()
  const { openToast } = useToast()
  const { getAfter7Days } = DateUtils
  const { autoOpenKakaoShare, resetAutoOpenKakaoShare } = useGlobalStore()
  const [giftIdx, setGiftIdx] = useState(0)
  const [transMsgIdx, setTransMsgIdx] = useState(0)
  const isGiftPaymentStatus = giftPaymentStatus.includes(paymentStatus)
  const isGiftSendStatus = giftSendStatus.includes(paymentStatus)
  const exceptionIdx = Number(exception_Idx)
  const kakaoScriptStatus = useScript('https://t1.kakaocdn.net/kakao_js_sdk/2.7.5/kakao.min.js', {
    integrity: 'sha384-dok87au0gKqJdxs7msEdBPNnKSRT+/mhTVzq+qOhcL464zXwvcrpjeWvyj1kCdq6',
    crossOrigin: 'anonymous',
  })

  // usePageVisibility((isForeground: boolean) => {
  //   if (isForeground && paymentStatus === 'payment_cancel') {
  //     handleBillgatePaymentRequest(orderId)
  //   }
  // })

  const { data: giftDetailByOrderIdQueryData, isLoading: isGiftDetailByOrderIdLoading } =
    useCustomQuery<GiftDetailInfoByOrderIdSpace.Response>(
      giftDetailInfoByOrderIdQueryOption({ OrderID: orderId }, isGiftPaymentStatus),
    )

  const { data: giftDetailByGiftIdxQueryData, isLoading: isGiftDetailByGiftIdxLoading } =
    useCustomQuery<GiftedCouponDetailSpace.Response>(
      giftedCouponDetailQueryOption({ GiftIdx: Number(orderId) }, isGiftSendStatus),
    )

  const { data: giftShareUrlQueryData, isLoading: isGiftShareUrlLoading } =
    useCustomQuery<GiftShareUrlInfoSpace.Response>(
      giftShareUrlInfoQueryOption({ GiftIdx: giftIdx }, !!giftIdx),
    )

  const giftDetailInfo = isGiftPaymentStatus
    ? giftDetailByOrderIdQueryData?.Body!
    : giftDetailByGiftIdxQueryData?.Body!

  const { SalePrice = 0 } = giftDetailInfo
  const SendType = (giftDetailInfo as GiftDetailInfoByOrderIdSpace.Response).SendType
  const RegDT = (giftDetailInfo as GiftDetailInfoByOrderIdSpace.Response).RegDT || DateUtils.now()
  const RecipientPNumber =
    (giftDetailInfo as GiftDetailInfoByOrderIdSpace.Response).RecipientPNumber || ''
  const after7Days = getAfter7Days(RegDT).format('MM.DD')
  const isKakaoSend = SendType === 0

  const validateGiftShareData = (): boolean => {
    const body = giftShareUrlQueryData?.Body
    const { Title, Contents, CardImg, GiftURL } = body ?? {}

    const isValid = !!Title && !!Contents && !!CardImg && !!GiftURL

    if (!isValid) {
      captureMessage('[GiftShare] 공유 데이터 없음', {
        level: SENTRY_SEVERITY.Info,
        extra: {
          giftIdx,
          hasTitle: !!Title,
          hasContents: !!Contents,
          hasCardImg: !!CardImg,
          hasGiftURL: !!GiftURL,
        },
      })
    }

    return isValid
  }

  const resultContents: {
    [key: string]: GiftResultContentType
  } = {
    payment_success: {
      title: '결제완료',
      desc: `${
        isKakaoSend
          ? '카카오톡 친구를 선택하여\n선물 링크를 공유해주세요.'
          : '입력한 번호로 선물 링크 문자를 보냈어요.'
      }`,
      infoList: [
        {
          text: '결제금액',
          value: Number(SalePrice)?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
        {
          text: '받는사람',
          value: isKakaoSend ? '카카오톡 링크 공유' : maskPhoneNumber(RecipientPNumber),
          bottomDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
      ],
      notiText: [
        `선물 받는 분이 ${after7Days} 까지 선물 받기를 완료하지 않을 경우 선물은 자동으로 취소됩니다.`,
        '오류로 결제가 정상 처리되지 않은 경우, 1~2분 내 자동으로 결제가 취소됩니다.',
        ...(!isKakaoSend
          ? ['선물 받는 분이 문자를 받지 못한 경우, 선물 내역에서 링크를 다시 공유할 수 있습니다.']
          : []),
      ],
      share: true,
      afterPurchaseButton: {
        text: '선물 내역 확인',
        onClickFn: giftedCouponPageNav,
      },
    },
    payment_fail: {
      title: '결제실패',
      desc: '결제에 실패했습니다.',
      infoList: [
        {
          text: '결제금액',
          value: Number(SalePrice)?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
        {
          text: '받는사람',
          value: isKakaoSend ? '카카오톡 링크 공유' : maskPhoneNumber(RecipientPNumber),
          bottomDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
      ],
      notiText: ['오류로 결제가 정상처리되지 않은 경우, 1~2분 내 자동으로 결제가 취소됩니다.'],
      afterPurchaseButton: {
        text: '다시 구매하기',
        onClickFn: () => giftPurchasePageNav(Number(couponType), exceptionIdx, couponNumber),
      },
    },
    payment_cancel: {
      title: '결제취소',
      desc: '결제가 취소되었습니다.',
      infoList: [
        {
          text: '결제금액',
          value: Number(SalePrice)?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
        {
          text: '받는사람',
          value: isKakaoSend ? '카카오톡 링크 공유' : maskPhoneNumber(RecipientPNumber),
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
      ],
      afterPurchaseButton: {
        text: '다시 구매하기',
        onClickFn: () => giftPurchasePageNav(Number(couponType), exceptionIdx, couponNumber),
      },
    },
    send_complete: {
      title: '선물완료',
      desc: `${
        isKakaoSend
          ? '카카오톡 친구를 선택하여\n선물 링크를 공유해주세요.'
          : '입력한 번호로 선물 링크 문자를 보냈어요.'
      }`,
      infoList: [
        {
          text: '결제금액',
          value: Number(SalePrice)?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
        {
          text: '받는사람',
          value: isKakaoSend ? '카카오톡 링크 공유' : maskPhoneNumber(RecipientPNumber),
          bottomDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
      ],
      notiText: [
        `선물 받는 분이 ${after7Days} 까지 선물 받기를 완료하지 않을 경우 선물은 구매내역으로 복원됩니다.`,
        ...(!isKakaoSend
          ? ['선물 받는 분이 문자를 받지 못한 경우, 선물 내역에서 링크를 다시 공유할 수 있습니다.']
          : []),
      ],
      share: true,
      afterPurchaseButton: {
        text: '선물 내역 확인',
        onClickFn: giftedCouponPageNav,
      },
    },
  }
  const resultContent = resultContents[paymentStatus]

  const handleShareUrl = async (url: string) => {
    if (web) {
      try {
        await navigator.share({ url: url })
      } catch (error) {
        await navigator.clipboard.write([new ClipboardItem({ 'text/plain': url })])
        openToast({ contents: 'url이 클립보드에 복사되었습니다.' })
      }
    } else {
      Bridge.handleReqSystemShare(url)
    }
  }

  const handleIosKakaoShare = () => {
    validateGiftShareData()
    if (ios) {
      try {
        Bridge.handleReqGiftKakaoShare(JSON.stringify(giftShareUrlQueryData?.Body))
      } catch (error) {
        captureError(toBridgeError(new Error(String(error))), {
          level: SENTRY_SEVERITY.Warning,
        })
      }
    }
  }

  //공유하기 데이터 받아오기(GiftIdx 업데이트 -> 데이터 받아오기 -> 버튼 생성)
  useEffect(() => {
    const giftData = giftDetailByGiftIdxQueryData?.Body ?? giftDetailByOrderIdQueryData?.Body
    if (!!giftData) {
      if (!giftIdx) setGiftIdx(giftData.GiftIdx)
      if (!transMsgIdx) setTransMsgIdx(giftData.TransMsgIdx)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftDetailByOrderIdQueryData, giftDetailByGiftIdxQueryData])

  // 카카오 공유하기 초기 세팅
  useEffect(() => {
    if (kakaoScriptStatus === 'error') {
      captureMessage('[GiftShare] 카카오 SDK 스크립트 로드 실패', {
        level: SENTRY_SEVERITY.Info,
        extra: { kakaoScriptStatus },
      })
      return
    }

    if (kakaoScriptStatus === 'ready' && !window.Kakao?.isInitialized()) {
      try {
        window.Kakao.init(process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY)
      } catch (error) {
        captureError('[GiftShare] Kakao.init 실패', {
          level: SENTRY_SEVERITY.Info,
          extra: {
            error: String(error),
          },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kakaoScriptStatus, window.kakao])

  useEffect(() => {
    if (!!giftShareUrlQueryData) {
      validateGiftShareData()

      if (!ios && !!window.Kakao?.isInitialized()) {
        const { Title, Contents, CardImg, GiftURL } = giftShareUrlQueryData.Body

        const kakaoShareInfo = {
          objectType: 'feed',
          content: {
            title: Title,
            description: Contents,
            imageUrl: CardImg,
            link: {
              mobileWebUrl: GiftURL,
              webUrl: GiftURL,
            },
          },
          buttons: [
            {
              title: '선물받기',
              link: {
                mobileWebUrl: GiftURL,
                webUrl: GiftURL,
              },
            },
          ],
          installTalk: true,
        }
        try {
          window.Kakao.Share.createDefaultButton({
            container: '#kakaotalk-share-btn',
            ...kakaoShareInfo,
          })
        } catch (error) {
          captureError('[GiftShare] 공유 버튼 생성 실패', {
            level: SENTRY_SEVERITY.Warning,
            extra: {
              error: String(error),
            },
          })
        }
      }

      if (autoOpenKakaoShare) {
        setTimeout(() => {
          document.getElementById('kakaotalk-share-btn')?.click()
        }, 500)
      }
      resetAutoOpenKakaoShare()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftShareUrlQueryData, window.Kakao])

  useEffect(() => {
    if (paymentStatus === 'payment_fail' || paymentStatus === 'purchase_cancel') {
      resetAutoOpenKakaoShare()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus])

  const props: GiftPaymentPageProps = {
    giftDetailData: giftDetailInfo!,
    giftShareData: giftShareUrlQueryData?.Body!,
    resultContent,
    mainPageNav,
    handleShareUrl,
    handleIosKakaoShare,
  }

  return (
    <React.Fragment>
      <RenderIf
        when={isGiftDetailByOrderIdLoading || isGiftDetailByGiftIdxLoading || isGiftShareUrlLoading}
      >
        <Loading.Default />
      </RenderIf>
      <GiftPaymentPageView {...props} />
    </React.Fragment>
  )
}
export default GiftPaymentPage
